/**
 * MELYIK GPS-PONT HASZNÁLHATÓ, ÉS MELYIK CSAK ÚJRABEMÉRÉS.
 *
 * ⚠️ MÉRT ESET (tesztelő, 2026-09-14, séta, `c9837182`): hazaérve, liftben
 * felfelé a telefon elveszti a GPS-t, és wifi/cella alapján méri be magát
 * újra. A nyomvonal végén ettől 510 m, 504 m és 249 m ugrás keletkezett,
 * közben egy másodperc alatt 32 méteres lépések. A Trust Score teleport-jele
 * 0%-ot adott, és ~1,3 km hamis táv került a sétába.
 *
 * ⚠️ A KÜLÖNBSÉG A PONTOSSÁGBAN LÁTSZIK, NEM A SEBESSÉGBEN. A valódi séta
 * végig 3-8 méteres fixekkel ment, az ugrások 13-28 méteresekkel — mind a 30
 * méteres elfogadási határ ALATT, ezért mentek át. A sebesség viszont nem
 * árulkodó: a 48 másodperces szünet után az 510 méteres ugrás „csak" 10,7
 * m/s, ami a rögzítő 40 m/s-os plafonja alatt van.
 *
 * ⚠️ EZ NEM SPORTÁGI HATÁR, és szándékosan nem is az: a szabály annyi, hogy
 * egy bizonytalan fix nem állíthat magáról nagyobb elmozdulást, mint a SAJÁT
 * bizonytalansága. Ezért nem mondja felül azt a döntést, hogy a rögzítő
 * sebességplafonja mozgásforma-független (`src/tracking/filter.ts`).
 *
 * ⚠️ EGY HELYEN LAKIK, mert három számítás függ tőle: az élő rajz, a
 * cellalánc (terület) és a megtett táv. Ha ezek eltérő szabállyal szűrnének,
 * a felhasználó mást látna menet közben, mint a végeredményben — ez a hiba
 * egyszer már megtörtént (lásd `src/tracking/filter.ts` fejlécét).
 */

import { GAMEPLAY } from '@/config/gameplay';
import { distanceM, type LatLng } from './geo';

export interface QualityPoint extends LatLng {
  /** Vízszintes pontosság méterben. Hiánya nem hiba: régi nyomvonalakon nincs. */
  accuracy?: number;
}

/**
 * Újrabemérés-e ez a pont az előzőhöz képest?
 *
 * Két feltétel EGYÜTT: a fix pontossága gyenge (a küszöb fölött), ÉS az
 * elmozdulás nagyobb, mint amennyit a saját bizonytalansága megenged. Egyik
 * sem elég önmagában — egy pontatlan, de helyben maradó fix rendben van, és
 * egy pontos, nagy elmozdulás valódi mozgás.
 *
 * `accuracy` nélküli ponton (régi nyomvonal) SOSEM szűr: amit nem tudunk, azt
 * nem büntetjük.
 */
export function isRelocationJump(previous: LatLng | undefined, point: QualityPoint): boolean {
  if (previous === undefined) return false;
  const accuracy = point.accuracy;
  if (accuracy === undefined || !Number.isFinite(accuracy)) return false;
  if (accuracy <= GAMEPLAY.RELOCATION_ACCURACY_M) return false;
  return distanceM(previous, point) > accuracy;
}

/**
 * Használható-e a pont önmagában (a szomszédaitól függetlenül)?
 *
 * Ez a régi, egyszerű pontossági kapu — külön függvényként, hogy a hívók ne
 * írják le harmadszor is ugyanazt a küszöböt.
 */
export function isAccurateEnough(point: QualityPoint): boolean {
  return point.accuracy === undefined || point.accuracy <= GAMEPLAY.MAX_GPS_ACCURACY_M;
}

/**
 * A nyomvonal használható pontjai, időrendben.
 *
 * ⚠️ AZ ELŐZŐ PONT MINDIG AZ UTOLSÓ ELFOGADOTT, nem a nyers előző: enélkül
 * egy elvetett ugrás után a következő pont ahhoz mérné magát, amit épp
 * eldobtunk, és a lánc végigcsúszna a hibás helyre.
 */
export function usableTracePoints<T extends QualityPoint>(points: readonly T[]): T[] {
  const out: T[] = [];
  let previous: T | undefined;
  for (const point of points) {
    if (!isAccurateEnough(point)) continue;
    if (isRelocationJump(previous, point)) continue;
    out.push(point);
    previous = point;
  }
  return out;
}
